import axios from 'axios';
import { OpenAPI } from '../api/core/OpenAPI';
import { store } from '../redux/store';
import type { CustomerSalesHistoryItem, CustomerPostSaleStatus } from '../types/sales.types';

export interface SellerListFilterParams {
  page?: number;
  limit?: number;
  searchTerm?: string;
  locationId?: number;
  saleDateFrom?: string;
  saleDateTo?: string;
}

export interface SellerListRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  isActive: boolean;
  locationId: number | null;
  locationName: string | null;
  totalSales: number;
  salesCount: number;
  createdAt: string;
}

export interface SellerListResponse {
  data: SellerListRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SellerByLocation {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = store.getState().auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQueryParams(filters: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });
  return params;
}

const isPostSaleStatus = (v: unknown): v is CustomerPostSaleStatus =>
  v === 'NONE' || v === 'REFUNDED' || v === 'EXCHANGED' || v === 'REFUNDED_AND_EXCHANGED';

function mapSalesHistoryRow(row: unknown): CustomerSalesHistoryItem | null {
  if (row == null || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const createdRaw = String(r.createdAt ?? '');
  const saleDateRaw = String(r.saleDate ?? createdRaw);
  return {
    orderId: Number(r.orderId ?? 0),
    orderCode: String(r.orderCode ?? ''),
    branchReceiptCode:
      r.branchReceiptCode == null || r.branchReceiptCode === ''
        ? null
        : String(r.branchReceiptCode),
    saleDate: new Date(saleDateRaw),
    createdAt: new Date(createdRaw),
    totalAmount: Number(r.totalAmount ?? 0),
    orderStatus: String(r.orderStatus ?? ''),
    locationName: r.locationName == null ? null : String(r.locationName),
    hasRefund: Boolean(r.hasRefund),
    hasExchange: Boolean(r.hasExchange),
    postSaleStatus: isPostSaleStatus(r.postSaleStatus) ? r.postSaleStatus : 'NONE',
  };
}

export const sellersService = {
  async list(filters: SellerListFilterParams): Promise<SellerListResponse> {
    const params = buildQueryParams({ ...filters });
    const response = await axios.get<SellerListResponse>(
      `${OpenAPI.BASE}/sellers?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  async getById(id: string): Promise<SellerListRow> {
    const response = await axios.get<SellerListRow>(`${OpenAPI.BASE}/sellers/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  async getByLocation(locationId: number): Promise<SellerByLocation[]> {
    const params = buildQueryParams({ locationId });
    const response = await axios.get<SellerByLocation[]>(
      `${OpenAPI.BASE}/sellers/by-location?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  async getSalesHistory(
    sellerId: string,
    params?: { page?: number; limit?: number },
  ): Promise<{
    data: CustomerSalesHistoryItem[];
    total: number;
    page: number;
    limit: number;
  }> {
    const query = buildQueryParams({ page: params?.page, limit: params?.limit });
    const { data: payload } = await axios.get<{
      data?: unknown[];
      total?: unknown;
      page?: unknown;
      limit?: unknown;
    }>(`${OpenAPI.BASE}/sellers/${sellerId}/sales-history?${query.toString()}`, {
      headers: getAuthHeaders(),
    });

    const rawRows = Array.isArray(payload?.data) ? payload.data : [];
    return {
      data: rawRows
        .map(mapSalesHistoryRow)
        .filter((item): item is CustomerSalesHistoryItem => item != null),
      total: Number(payload?.total ?? 0),
      page: Number(payload?.page ?? params?.page ?? 1),
      limit: Number(payload?.limit ?? params?.limit ?? 20),
    };
  },
};

export default sellersService;
